import { useState, useEffect, useRef } from 'react';
import { Input } from './ui/input';

// Agrupa la parte entera con punto de miles: "1234567" -> "1.234.567"
const groupInt = (digits) => (digits || '').replace(/\B(?=(\d{3})+(?!\d))/g, '.');

// Formatea un valor numérico (string con punto decimal o number) a "$ 1.234,56"
export const formatMoneyDisplay = (numericVal) => {
  if (numericVal === '' || numericVal === null || numericVal === undefined) return '';
  let s = String(numericVal);
  if (s === '' || isNaN(Number(s))) return '';
  let [intp, decp] = s.split('.');
  intp = (intp || '0').replace(/[^\d-]/g, '');
  const neg = intp.startsWith('-');
  intp = intp.replace('-', '');
  let out = (neg ? '-$ ' : '$ ') + groupInt(intp || '0');
  if (decp !== undefined && decp !== '') out += ',' + decp.slice(0, 2);
  return out;
};

/**
 * Input de importes dinerarios.
 * - Muestra signo $, miles con punto y decimales con coma (máx 2 decimales).
 * - Mantiene en el estado del padre un string numérico con punto decimal (ej "5000.5"),
 *   por lo que `parseFloat(value)` sigue funcionando igual que con <Input type="number">.
 * - Reemplazo casi directo: conserva la firma `value` / `onChange(e)` con e.target.value numérico.
 * - `allowNegative` (opt-in): permite tipear "-" al inicio para valores negativos.
 *   Ideal para ajustes de redondeo (ej: "No gravado" en compras).
 */
export const MoneyInput = ({ value, onChange, className, allowNegative = false, ...props }) => {
  const [display, setDisplay] = useState(formatMoneyDisplay(value));
  const focusedRef = useRef(false);

  useEffect(() => {
    if (!focusedRef.current) setDisplay(formatMoneyDisplay(value));
  }, [value]);

  const handleChange = (e) => {
    const rawInput = e.target.value || '';
    // Detectar negativo por "-" al inicio (sólo si allowNegative)
    const isNegative = allowNegative && rawInput.trim().startsWith('-');
    let raw = rawInput.replace(/[^\d,]/g, '');
    const parts = raw.split(',');
    let intDigits = (parts[0] || '').replace(/^0+(?=\d)/, '');
    let dec = parts.length > 1 ? parts[1].slice(0, 2) : null;
    const hasContent = intDigits !== '' || dec !== null;
    const sign = isNegative && hasContent ? '-' : '';
    const numeric = !hasContent
      ? ''
      : `${sign}${intDigits || '0'}${dec !== null ? '.' + dec : ''}`;
    let disp;
    if (intDigits === '') {
      disp = parts.length > 1 ? (sign ? '-$ 0' : '$ 0') : (sign || '');
    } else {
      disp = (sign ? '-$ ' : '$ ') + groupInt(intDigits);
    }
    if (dec !== null) disp += ',' + dec;
    setDisplay(disp);
    if (onChange) onChange({ target: { value: numeric } });
  };

  // Numpad "," en algunos teclados/OS emite ".". Interceptamos y convertimos a coma decimal.
  const handleKeyDown = (e) => {
    if (e.key === '.') {
      e.preventDefault();
      const target = e.target;
      const start = target.selectionStart ?? target.value.length;
      const end = target.selectionEnd ?? target.value.length;
      const newValue = target.value.slice(0, start) + ',' + target.value.slice(end);
      handleChange({ target: { value: newValue } });
    }
    if (props.onKeyDown) props.onKeyDown(e);
  };

  return (
    <Input
      {...props}
      type="text"
      inputMode="decimal"
      value={display}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onFocus={(e) => { focusedRef.current = true; if (props.onFocus) props.onFocus(e); }}
      onBlur={(e) => { focusedRef.current = false; setDisplay(formatMoneyDisplay(value)); if (props.onBlur) props.onBlur(e); }}
      className={className}
    />
  );
};

export default MoneyInput;
